import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, Upload, CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export const LATAMRegionPage = () => {
  const countries = [
    { name: 'Mexico', slug: 'mexico-pharma-nutra-registration' },
    { name: 'Colombia', slug: 'colombia-pharma-nutra-registration' },
    { name: 'Peru', slug: 'peru-pharma-nutra-registration' },
    { name: 'Chile', slug: 'chile-pharma-nutra-registration' },
    { name: 'Dominican Republic', slug: 'dominican-republic-pharma-nutra-registration' },
    { name: 'Panama', slug: 'panama-pharma-nutra-registration' },
    { name: 'Paraguay', slug: 'paraguay-pharma-nutra-registration' },
    { name: 'Costa Rica', slug: 'costa-rica-pharma-nutra-registration' },
    { name: 'Uruguay', slug: 'uruguay-pharma-nutra-registration' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>LATAM Pharma Registration | Mexico Colombia Peru Chile CTD Dossier Regulatory Affairs</title>
        <meta
          name="description"
          content="Expert CTD-aligned dossier services for pharmaceutical and nutraceutical registration across Latin America. Mexico, Colombia, Peru, Chile, Dominican Republic compliance."
        />
        <meta name="keywords" content="LATAM pharma registration, mexico pharma registration, colombia dossier, peru regulatory affairs, chile registration, latin america pharmaceutical, CTD dossier latam, spanish artwork compliance, latam nutra registration, pharmaceutical exports latin america" />
        
        <meta property="og:title" content="LATAM Pharma Registration | Mexico Colombia Peru Chile Regulatory Affairs" />
        <meta property="og:description" content="CTD-aligned dossiers and compliance support for pharmaceutical and nutraceutical products across Latin America markets." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6" style={{ backgroundColor: '#606090', color: 'white' }}>
            LATAM Region
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            Regulatory Affairs in LATAM
          </h1>
          <p className="text-2xl mb-4" style={{ color: '#505060' }}>
            Your partner for CTD-aligned dossiers and compliance across Mexico, Peru, Chile, Colombia, Dominican Republic, Paraguay, Costa Rica & Uruguay.
          </p>
          <p className="text-xl mb-12" style={{ color: '#606070' }}>
            LATAM markets are evolving rapidly: modernizing regulations, increasing import reliance, and creating new opportunities for compliant pharma & nutra exporters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('countries').scrollIntoView({ behavior: 'smooth' })} style={{ backgroundColor: '#606090', color: 'white' }} className="text-lg px-8 py-6">
              Explore LATAM Countries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" style={{ borderColor: '#606090', color: '#606090' }}>
                Book a Regional Strategy Call
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why LATAM Matters */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why LATAM Matters
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            LATAM offers:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Large populations',
              'Growing middle class',
              'High NCD prevalence (metabolic, cardiac, obesity)',
              'Modernization of regulatory frameworks',
              'Sophisticated supplement market segments',
              'Rising demand for imported chronic therapies'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: '#F0E0D0' }}>
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#606090' }} />
                <span className="text-lg" style={{ color: '#303040' }}>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xl mt-8 text-center font-medium" style={{ color: '#606090' }}>
            For companies entering multiple markets, LATAM provides scale and long-term growth.
          </p>
        </div>
      </section>

      {/* Regulatory Landscape */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Regulatory Landscape
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>CTD Aligned</h3>
              <p style={{ color: '#505060' }}>Most markets use CTD-aligned dossiers with Spanish-language labeling mandatory</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Strict Standards</h3>
              <p style={{ color: '#505060' }}>Artwork claims must meet strict standards. Some markets use reliance pathways (Chile, Peru)</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Supplements</h3>
              <p style={{ color: '#505060' }}>Certain regions allow simplified routes for supplements</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Harmonization</h3>
              <p style={{ color: '#505060' }}>Brazil-like standards influence many LATAM countries indirectly</p>
            </div>
          </div>
          <p className="text-center mt-8 text-lg" style={{ color: '#606070' }}>
            We help keep documentation consistent while adapting for local nuances.
          </p>
        </div>
      </section>

      {/* Countries Covered */}
      <section id="countries" className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Countries Covered
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map((country) => (
              <Link
                key={country.slug}
                to={`/country/${country.slug}`}
                className="group block p-6 bg-white rounded-2xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                style={{ borderLeft: '4px solid #606090' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold" style={{ color: '#303040' }}>{country.name}</span>
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" style={{ color: '#606090' }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            What TheApostrophe Does in LATAM
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'CTD-aligned dossier compilation',
              'Module 1 Spanish localization',
              'Artwork, translations & packaging compliance',
              'Importer coordination',
              'Query/deficiency handling',
              'Supplement classification & ingredient review'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#606090' }} />
                <span className="text-lg" style={{ color: '#505060' }}>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Products */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Ideal Product Categories
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Pharma</h3>
              <ul className="space-y-2">
                {['Antidiabetics', 'Antihypertensives', 'Specialty therapies', 'GI & liver health', 'Vitamin/mineral therapeutics'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#606090' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Nutra</h3>
              <ul className="space-y-2">
                {['Metabolic support', 'Cardiovascular supplements', 'Beauty & antioxidant products', 'Senior health & mobility', 'Women\'s wellness & PCOS'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#606090' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#303040' }}>
              LATAM Registration FAQs
            </h2>
            <p className="text-lg" style={{ color: '#708080' }}>
              Common questions about Latin America pharmaceutical registration
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Do all LATAM countries require Spanish translations?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  Yes. <strong>Spanish translations</strong> are mandatory for labels and package inserts across all LATAM countries including Mexico, Colombia, Peru, Chile, and others. Some countries like <strong>Dominican Republic</strong> may also require Portuguese for certain regions. We provide regulatory-grade Spanish translations with proper pharmaceutical terminology.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Which LATAM countries have the fastest registration timelines?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Paraguay and Panama</strong> offer relatively faster timelines (6-9 months). <strong>Peru and Chile</strong> have modernized systems with 9-12 month timelines. <strong>Mexico and Colombia</strong> can take 12-18 months due to more comprehensive reviews. All timelines depend on dossier completeness and local importer coordination.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Can I use the same CTD dossier for all LATAM countries?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Modules 2-5 remain largely consistent</strong> across LATAM countries. However, <strong>Module 1 (administrative section)</strong> requires country-specific customization including local importer details, artwork, translations, and regulatory forms. We create a master CTD dossier and customize Module 1 for each target country, maximizing efficiency.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #606090 0%, #7070A0 100%)' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start your LATAM regulatory journey
          </h2>
          <p className="text-2xl mb-8 text-white/90">
            With a structured, country-specific roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" className="text-lg px-8 py-6 bg-white hover:bg-white/90" style={{ color: '#606090' }}>
                Start LATAM Expansion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                <Upload className="mr-2 h-5 w-5" />
                Upload Product List
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
